import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { AsamcoUserService } from '@asamco/base';
import { isNullOrWhitespace, pathJoin, showErrorSwal } from '@asamco/utils';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { LinkStatusCode } from '../../enums/link-status-code.enum';
import { Link } from '../../interfaces/link.interface';
import { MarketPlaceItem } from '../../interfaces/market-place-item.interface';
import { LinkDescriptionService } from '../../services/link-description.service';
import { LinkStatusSignalrService } from '../../services/signalR/link-status-signalr.service';
import { LinkEditInfoModalComponent } from '../link-edit-info-modal/link-edit-info-modal.component';
import { LinkEditTitleModalComponent } from '../link-edit-title-modal/link-edit-title-modal.component';
import { Router } from '@angular/router';
import { LinkRunStatus } from '../../enums/link-run-status.enum';
import { FunctionAppInfo } from '../../interfaces/function-app-info.interface';
import { QueueService } from '../../services/queue.service';
import { Queue, QueueTrigger } from '../../interfaces/queue.interface';
import { NgIf, NgClass, AsyncPipe, CommonModule } from '@angular/common';
import { FunctionAppsLinkService } from '../../services/function-apps-link.service';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { UsermanagementService } from '../../services/usermanagement.service';

@Component({
	selector: 'al-link-header',
	templateUrl: './link-header.component.html',
	styleUrls: ['../../styles/alert.scss', './link-header.component.scss'],
	standalone: true,
	imports: [
		NgIf,
		NgClass,
		LinkEditInfoModalComponent,
		LinkEditTitleModalComponent,
		AsyncPipe,
		CommonModule
	],
})
export class LinkHeaderComponent implements OnInit, OnDestroy {
	public imgUrl = '';

	@ViewChild(LinkEditInfoModalComponent)
	editInfoModal: LinkEditInfoModalComponent;

	@ViewChild(LinkEditTitleModalComponent)
	editTitleModal: LinkEditTitleModalComponent;

	/** The link to edit */
	@Input()
	link: Link;

	/** The link information*/
	@Input()
	linkInfo: MarketPlaceItem;

	public canRunLinks$ = this.userManagementService.checkCanRunLinks().pipe(shareReplay(1));

	public admin: boolean = false;
	public disabledRunLinkButtonSubject = new BehaviorSubject<LinkRunStatus>(LinkRunStatus.undefined);
	public disabledRunLinkButtonObservable: Observable<LinkRunStatus> = this.disabledRunLinkButtonSubject.asObservable();
	public customFunctionAppInfo$: Subject<FunctionAppInfo | null> = new BehaviorSubject<FunctionAppInfo | null>(null);

	//public currentStatus: string;

	public currentStatusSubject: Subject<string> = new BehaviorSubject<string>('There is currently no live status');
	public currentStatusColorSubject: Subject<LinkStatusCode> = new BehaviorSubject<LinkStatusCode>(LinkStatusCode.Undefined);

	linkStatusCode = LinkStatusCode;

	public statusCode: LinkStatusCode;
	private debounceTime: number = 1000;

	private destroy$ = new Subject<void>();

	constructor(
		private linkDescriptionService: LinkDescriptionService,
		private asamcoUserService: AsamcoUserService,
		private linkStatusService: LinkStatusSignalrService,
		private queueService: QueueService,
		private router: Router,
		private functionAppsLinkService: FunctionAppsLinkService,
		private selectedCompanyService: SelectedCompanyService,
		private userManagementService: UsermanagementService
	) {
		this.imgUrl = pathJoin([window['deployUrl'], '/assets/img/']);
	}

	public get linkRunStatus(): typeof LinkRunStatus {
		return LinkRunStatus;
	}

	ngOnInit(): void {
		this.asamcoUserService.isAdmin().then(result => (this.admin = result));

		//No need to retrieve beta banner information if we're not an integration-type link
		if (!this.linkInfo.frontendPage) {
			this.getFunctionAppInfoData();
		}
		this.currentStatusSubject.next(this.link.statusLabel);
		this.currentStatusColorSubject.next(this.link.statusColor);

		this.queueService.isLinkRunning(this.link.id).subscribe({
			next: isLinkRunning => {
				if (isLinkRunning) {
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
				} else {
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
				}
			},
			error: error => {
				showErrorSwal(error);
			}
		});

		this.linkStatusService.startService().subscribe(() => {
			this.linkStatusService.joinCompany(this.link.companyId);

			this.linkStatusService.getLinkButtonObservable()
				.pipe(
					takeUntil(this.destroy$),
					filter(linkStatus => linkStatus.linkID === this.link.id)
				)
				.subscribe(linkButtonStatus => {
					const status = linkButtonStatus.isLinkRunning
						? LinkRunStatus.active
						: LinkRunStatus.inactive;

					this.disabledRunLinkButtonSubject.next(status);
				});

			this.linkStatusService.getLinkStatusObservable()
				.pipe(
					takeUntil(this.destroy$),
					filter(linkStatus => linkStatus.linkID === this.link.id)
				)
				.subscribe(linkStatus => {
					if (linkStatus.statusCode !== LinkStatusCode.Undefined) {
						this.statusCode = linkStatus.statusCode;
						this.currentStatusSubject.next(linkStatus.status);
						this.currentStatusColorSubject.next(linkStatus.statusCode);
					}
				});
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public getUrl(subUrl: string): string {
		if (!isNullOrWhitespace(this.imgUrl) && !isNullOrWhitespace(subUrl)) {
			return pathJoin([this.imgUrl, subUrl]);
		}
		return '';
	}

	getFunctionAppInfoData() {
		this.selectedCompanyService.getCompanyIdObservable()
			.pipe(
				switchMap((companyId: number) =>
					this.functionAppsLinkService.GetCustomLinkConfigByLink(companyId, this.link.id)
				)
			)
			.subscribe((functionAppInfos: any[]) => {
				if (functionAppInfos && functionAppInfos.length > 0) {
					const functionAppInfo = {
						globalApp: functionAppInfos.find(item => item.originType === 'Global'),
						sourceApp: functionAppInfos.find(item => item.originType === 'Source'),
						targetApp: functionAppInfos.find(item => item.originType === 'Target'),
						entryPointApp: functionAppInfos.find(item => item.originType === 'Entrypoint'),
					};
					this.customFunctionAppInfo$.next(functionAppInfo);
				}
			});
	}

	openModalShort(): void {
		if (this.admin === false) return;
		else {
			this.editInfoModal
				.show('short description', this.linkInfo.shortDescription)
				.pipe(takeUntil(this.destroy$))
				.subscribe(result => {
					this.linkInfo.shortDescription = result;
					this.linkDescriptionService.setShortDescription(this.linkInfo.id, result).pipe(takeUntil(this.destroy$)).subscribe({ error: console.error });
				});
		}
	}

	openModalTitle(): void {
		if (this.admin === false) return;
		else {
			this.editTitleModal
				.show(this.linkInfo.displayName)
				.pipe(takeUntil(this.destroy$))
				.subscribe(result => {
					this.linkInfo.displayName = result;
					this.linkDescriptionService.editTitle(this.linkInfo.id, result).pipe(takeUntil(this.destroy$)).subscribe({ error: console.error });
				});
		}
	}

	pressPlay(): void {
		if (this.linkInfo.frontendPage != null && this.linkInfo.frontendPage.length > 0) {
			this.toFrontendPage();
		} else {
			this.runLink();
		}
	}

	toFrontendPage(): void {
		this.router.navigate([this.linkInfo.frontendPage, this.link.id]);
	}

	runLink(): void {
		this.disabledRunLinkButtonSubject.next(LinkRunStatus.active);
		this.currentStatusSubject.next('Link enqueued, awaiting execution');
		this.currentStatusColorSubject.next(LinkStatusCode.Info);

		const queue: Queue = {
			linkId: this.link.id,
			trigger: QueueTrigger.User,
		};

		this.queueService.createQueueEntry(queue).subscribe(
			(result: any) => {
				if (result.statusCode == 500) {
					this.currentStatusSubject.next('Failed to start link');
					// this.currentStatus = 'Failed to start link';
					this.statusCode = LinkStatusCode.Danger;
					this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
				}
				// Handle result
				//Keep running if everything went ok
			},
			error => {
				//TODO: Detailed information?
				this.currentStatusSubject.next('Failed to start link');
				//this.currentStatus = 'Failed to start link';
				this.statusCode = LinkStatusCode.Danger;
				this.disabledRunLinkButtonSubject.next(LinkRunStatus.inactive);
				showErrorSwal(error);
			},
			() => {
				// 'onCompleted' callback.
				// No errors, route to new page here
			}
		);
	}

	hasPassed(date: Date): boolean {
		const now = new Date();
		return date < now;
	}
}
